/**
 * Content Analyzer
 * Evaluates the main body of the blog for SEO structure and quality.
 */

const getWordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

// Extract text content from basic HTML or simple text
const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, ' ');
};

export const analyzeContent = (data) => {
  const { content = "" } = data;
  let score = 0;
  let maxScore = 20;
  const results = [];

  const cleanContent = stripHtml(content);
  const wordCount = getWordCount(cleanContent);

  if (wordCount === 0) {
    return {
      score: 0,
      maxScore,
      results: [{ status: "error", text: "Content is empty." }]
    };
  }

  // 1. Minimum 800-1200 words
  if (wordCount >= 800 && wordCount <= 2000) {
    score += 5;
    results.push({ status: "success", text: `Content length is excellent (${wordCount} words).` });
  } else if (wordCount > 2000) {
    score += 4;
    results.push({ status: "warning", text: `Content is quite long (${wordCount} words). Ensure it remains engaging.` });
  } else if (wordCount >= 500) {
    score += 3;
    results.push({ status: "warning", text: `Content is acceptable (${wordCount} words), but 800+ words rank better.` });
  } else {
    results.push({ status: "error", text: `Content is too short (${wordCount} words). Aim for at least 800 words.` });
  }

  // 2. Paragraph length (no single paragraph > 150 words)
  // Split by double newline for simple text or by block tags like <p>, <div> for HTML
  let paragraphs = [];
  if (content.includes("<p>")) {
    const pMatches = content.match(/<p[^>]*>.*?<\/p>/gi);
    if (pMatches) paragraphs = pMatches.map(stripHtml);
  } else {
    paragraphs = content.split(/\n\s*\n/);
  }

  if (paragraphs.length > 0) {
    const longParagraphs = paragraphs.filter(p => getWordCount(p) > 150);
    if (longParagraphs.length === 0) {
      score += 4;
      results.push({ status: "success", text: "Paragraph lengths are optimal (easy to read)." });
    } else {
      results.push({ status: "warning", text: `${longParagraphs.length} paragraph(s) are too long. Keep them under 150 words.` });
    }
  }

  // 3. Presence of headings (H2/H3)
  const hasH2 = /<h2[^>]*>.*?<\/h2>/gi.test(content);
  const hasH3 = /<h3[^>]*>.*?<\/h3>/gi.test(content);
  
  if (hasH2) {
    score += 3;
    results.push({ status: "success", text: "Good use of H2 headings." });
  } else {
    results.push({ status: "error", text: "No H2 headings found. Use headings to structure your content." });
  }

  if (hasH2 && hasH3) {
    results.push({ status: "success", text: "Good heading structure (H2 and H3 present)." });
  }

  // 4. Presence of bullet lists
  const hasLists = /<ul[^>]*>.*?<\/ul>/gi.test(content) || /<ol[^>]*>.*?<\/ol>/gi.test(content);
  if (hasLists) {
    score += 2;
    results.push({ status: "success", text: "Content contains bulleted or numbered lists." });
  } else {
    results.push({ status: "warning", text: "Consider adding bulleted lists to break up text." });
  }

  // 5. Presence of images (within body)
  const hasImages = /<img[^>]*>/gi.test(content);
  if (hasImages) {
    score += 3;
    results.push({ status: "success", text: "Images found in the content." });
  } else {
    results.push({ status: "warning", text: "No images found in the body. Images improve engagement." });
  }

  // 6. Internal / External Links
  const links = content.match(/<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/gi) || [];
  if (links.length > 0) {
    score += 3;
    results.push({ status: "success", text: `Found ${links.length} links in the content.` });
    
    // Very basic check for external vs internal would require knowing the base domain
    // For this context, just finding links is a good start.
  } else {
    results.push({ status: "warning", text: "No outbound or internal links found in the content." });
  }

  return { score, maxScore, results };
};
